import { invalidateDocumentListData } from "@prismicio/editor-fields"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  IconButton,
  Tooltip,
  useAlert,
} from "@prismicio/editor-ui"
import type { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import { useQueryClient } from "@tanstack/react-query"
import { type TransitionStartFunction, useState, useTransition } from "react"

import { archiveDocument } from "../../app/services/core"
import {
  canRoleArchiveDocument,
  editDocumentPermission,
} from "../../app/services/permissions"
import {
  getReleaseId,
  invalidateReleaseSizeData,
} from "../../app/services/release"
import { useMyRole } from "../../app/services/role"
import { createDocument, revalidateDocumentMeta } from "../documentData"
import { useDocument } from "../DocumentProvider"
import { useGoToDocument } from "../documentRouting"
import { useSettingsMenu } from "./SettingsMenuProvider"
import { SimulatorURLDialog } from "./SimulatorURLDialog"

export function SettingsMenu() {
  const [isUpdating, startTransition] = useTransition()
  const [menuLoading, setMenuLoading] = useState(false)

  const itemProps: MenuItemProps = { setMenuLoading, startTransition }

  const { locale, teamSpace } = useDocument()

  const role = useMyRole({ localeId: locale, teamSpaceId: teamSpace.id })
  const canEditDocument = editDocumentPermission[role]

  return (
    <DropdownMenu>
      <Tooltip
        visible={!canEditDocument}
        title="You can't make changes"
        content="Your role is read-only. Only users with editing permission can make changes."
        side="bottom"
        stableMount
      >
        <DropdownMenuTrigger disabled={!canEditDocument}>
          <IconButton
            hiddenLabel="Document settings"
            icon="moreVert"
            size="large"
            loading={menuLoading || isUpdating}
            disabled={!canEditDocument}
          />
        </DropdownMenuTrigger>
      </Tooltip>

      <DropdownMenuContent align="end">
        <ArchiveDocumentItem {...itemProps} />
        <DuplicateDocumentItem {...itemProps} />
        <LivePreviewItem />
      </DropdownMenuContent>

      <SimulatorURLDialog />
    </DropdownMenu>
  )
}

interface MenuItemProps {
  setMenuLoading: (loading: boolean) => void
  startTransition: TransitionStartFunction
}

function ArchiveDocumentItem(props: MenuItemProps) {
  const { setMenuLoading, startTransition } = props
  const { data } = useDocument()

  const queryClient = useQueryClient()

  const canArchiveDocument = useCanArchiveDocument()

  const disabled =
    data.type === "new" ||
    data.version.status === "archived" ||
    !canArchiveDocument

  const { showAlert } = useAlert()

  function onArchiveDocument() {
    if (data.type !== "existing") return
    if (data.version.status === "archived") return

    setMenuLoading(true)

    const releaseId = getReleaseId(data.version)

    archiveDocument(data.meta.id, data.version.status, releaseId)
      .then(() => {
        startTransition(() => void revalidateDocumentMeta(data.meta.id))

        if (releaseId) {
          // When archiving a document already in a release, that particular release will not contain the document anymore => releases's size will change
          void invalidateReleaseSizeData(releaseId)
        }

        void invalidateDocumentListData(queryClient)
      })
      .catch(() =>
        showAlert({
          title: "Oops! We were unable to archive your document.",
          subtitle:
            "We encountered an error while archiving your document. Please contact our support team if this issue persists.",
          onRetry: onArchiveDocument,
        }),
      )
      .finally(() => setMenuLoading(false))
  }

  return (
    <Tooltip
      visible={!canArchiveDocument}
      title="You can't archive"
      content="Only users with the role of publisher or administrator can archive documents."
      side="left"
      stableMount
    >
      <DropdownMenuItem
        startIcon={<Icon name="inventory" />}
        onSelect={onArchiveDocument}
        disabled={disabled}
      >
        Archive document
      </DropdownMenuItem>
    </Tooltip>
  )
}

function DuplicateDocumentItem(props: MenuItemProps) {
  const { setMenuLoading } = props

  const { content, customType, draft, locale, type, documentTitle } =
    useDocument()

  const goToDocument = useGoToDocument()
  const { showAlert } = useAlert()

  const queryClient = useQueryClient()

  function onDuplicateDocument() {
    const contentWithoutUid = removeUUidFromContent(content)

    setMenuLoading(true)

    createDocument({
      customTypeId: customType.id,
      content: contentWithoutUid,
      title: documentTitle.draftTitle
        ? `Copy ${documentTitle.draftTitle}`
        : undefined,
      tags: Array.from(draft.documentTags),
      locale,
    })
      .then((newDocument) => {
        const newDraft = newDocument.versions.find(
          (version) => version.status === "unclassified",
        )
        if (!newDraft) return

        void invalidateDocumentListData(queryClient)

        draft.animateNextChange(newDraft.version_id)
        goToDocument({
          id: newDocument.id,
          status: newDraft.status,
          replace: false,
        })
      })
      .catch(() =>
        showAlert({
          title: "Oops! We were unable to duplicate your document.",
          subtitle:
            "We encountered an error while duplicating your document. Please contact our support team if this issue persists.",
          onRetry: onDuplicateDocument,
        }),
      )
      .finally(() => setMenuLoading(false))
  }

  const disabled = !customType.repeatable || type === "new"

  return (
    <Tooltip
      content="Duplication unavailable for single-page type."
      side="left"
      stableMount
      visible={disabled}
    >
      <DropdownMenuItem
        startIcon={<Icon name="contentCopy" />}
        onSelect={onDuplicateDocument}
        disabled={disabled}
      >
        Duplicate document
      </DropdownMenuItem>
    </Tooltip>
  )
}

function LivePreviewItem() {
  const settingsMenu = useSettingsMenu()

  return (
    <DropdownMenuItem
      startIcon={<Icon name="preview" />}
      onSelect={() => settingsMenu.openDialog("livePreview")}
    >
      Live preview settings
    </DropdownMenuItem>
  )
}

function removeUUidFromContent(content: DocumentT) {
  return Object.entries(content).reduce<DocumentT>(
    (contentAccumulator, [key, field]) => {
      if (field.__TYPE__ !== "UIDContent") contentAccumulator[key] = field

      return contentAccumulator
    },
    {},
  )
}

function useCanArchiveDocument() {
  const { data, locale, teamSpace } = useDocument()

  const role = useMyRole({ localeId: locale, teamSpaceId: teamSpace.id })

  if (data.type !== "existing") return false

  return canRoleArchiveDocument(role, data.version.status)
}
